"use client";

import {
  FC,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { MICRO_SECONDS_IN_SECOND } from "../utils/time";
import Badge from "./Badge";
import Typography from "./Typography";
import { PREVIEW_FPS } from "./previewPlayer";

// m:ss label. FLOORED, unlike the card player's rounding formatClock: this
// pill co-exists with the strip's DurationPills (hover-scan, Generate
// pre-flight — all floored), and a 25.93s short must not read 0:26 here and
// 0:25 there. Exported so the deck cards' stand-in pill (MomentCardStack)
// renders the exact same number and the thumb↔player hand-off never re-reads.
export const formatClock = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

type MomentPlayerScrubberProps = {
  /** Whether the playhead is inside the short this bar spans. False (blank
      track in browse, past the cut in Trim — where unfenced playback roams
      outside the range) hides the instrument entirely, hover or not. */
  visible: boolean;
  /** Drives the PILL's reveal: while playing it's hover-only (chrome yields
      to the content); paused/parked keeps it shown — the player is the
      current short's ONE at-rest duration readout (the strip's thumb pill is
      gesture-scoped). The bar ignores this: it's hover/drag-only in every
      state. */
  isPlaying: boolean;
  /** Active short's window, in full-source µs. */
  rangeStartUs: number;
  rangeEndUs: number;
  /** Committed playhead (full-source µs) — drives the rest fill + countdown. */
  currentTimeUs: number;
  /** Live drag preview: imperative frame move, no committed-state update
      (GenerateShortsDialog's previewSeekUs). raf-coalesced here. */
  onPreviewSeek: (us: number) => void;
  /** Drag release: commit the seek (GenerateShortsDialog's seekToUs). */
  onCommitSeek: (us: number) => void;
  /** The ridden card's media opacity (the dialog's playerContentOpacity), 1 at
      rest. The whole instrument rides it so the pill fades in lockstep with
      the deck card's stand-in pill during a feed scroll — without it the pill
      snapped to full opacity the instant the player took a mid-fade card over
      (the index-jump flicker). Applied on its own untransitioned wrapper so
      the per-frame scroll updates aren't smeared by the reveal transition. */
  contentOpacity?: number;
  /** Drop the bottom-right time/duration pill (bar only). @default true */
  showTimePill?: boolean;
  /** Fires true on drag start and false on release, so a host can swap
      chrome around the gesture (e.g. hide a social overlay while seeking). */
  onScrubbingChange?: (scrubbing: boolean) => void;
};

/**
 * Per-short progress instrument overlaid on the Timeline- and Trim-mode
 * player: a thin bottom-edge scrubber (the List-mode card's idiom — 3px track
 * that thickens on hover, white fill, tall hit zone) whose extent IS the
 * active short (in Trim, the focused draft's live range), plus the card's
 * time pill in the bottom-right corner (duration at rest, remaining while the
 * bar is out). Seeks are clamped to the short's window: crossing into
 * neighbours / gaps stays the timeline strip's job — as does resizing the
 * moment in Trim, so this bar can never move a cut.
 *
 * The pill lives here rather than beside the player because during a drag the
 * committed playhead doesn't move (preview seeks are imperative) — only the
 * bar's local drag fraction can drive a live countdown that tracks the thumb.
 */
const MomentPlayerScrubber: FC<MomentPlayerScrubberProps> = ({
  visible,
  isPlaying,
  rangeStartUs,
  rangeEndUs,
  currentTimeUs,
  onPreviewSeek,
  onCommitSeek,
  contentOpacity = 1,
  showTimePill = true,
  onScrubbingChange,
}) => {
  const spanUs = Math.max(1, rangeEndUs - rangeStartUs);
  const restFrac = Math.min(
    1,
    Math.max(0, (currentTimeUs - rangeStartUs) / spanUs),
  );

  // Local fill while dragging so only this bar re-renders per pointermove;
  // null at rest (committed playhead drives the fill).
  const [dragFrac, setDragFrac] = useState<number | null>(null);
  // Range frozen at drag start, so a mid-drag active-region change (shouldn't
  // happen — seeks are clamped inside the short — but e.g. an external draft
  // update) never warps the gesture's mapping under the pointer.
  const dragRangeRef = useRef<{ startUs: number; spanUs: number } | null>(null);
  // raf-coalesced preview: at most one imperative seek per frame, like the
  // dialog's hover-preview pipeline.
  const previewTargetUsRef = useRef<number | null>(null);
  const previewRafRef = useRef<number | null>(null);

  // Map a drag fraction to a clamped seek target: never past the last frame of
  // the short (same convention as togglePlayback's fence), never before its
  // start — the bar can't leave the short.
  const fracToSeekUs = useCallback((frac: number) => {
    const range = dragRangeRef.current;
    if (!range) return null;
    const oneFrameUs = MICRO_SECONDS_IN_SECOND / PREVIEW_FPS;
    const maxUs = Math.max(range.startUs, range.startUs + range.spanUs - oneFrameUs);
    return Math.min(maxUs, Math.max(range.startUs, range.startUs + frac * range.spanUs));
  }, []);

  const queuePreview = useCallback(
    (frac: number) => {
      const target = fracToSeekUs(frac);
      if (target === null) return;
      previewTargetUsRef.current = target;
      if (previewRafRef.current !== null) return;
      previewRafRef.current = requestAnimationFrame(() => {
        previewRafRef.current = null;
        const us = previewTargetUsRef.current;
        if (us !== null) onPreviewSeek(us);
      });
    },
    [fracToSeekUs, onPreviewSeek],
  );

  useEffect(
    () => () => {
      if (previewRafRef.current !== null) {
        cancelAnimationFrame(previewRafRef.current);
      }
    },
    [],
  );

  const fracFromClientX = (clientX: number, bar: HTMLElement): number => {
    const rect = bar.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Never reach the player's clickToPlay / other bottom-edge targets.
    e.stopPropagation();
    e.preventDefault();
    dragRangeRef.current = { startUs: rangeStartUs, spanUs };
    e.currentTarget.setPointerCapture(e.pointerId);
    const frac = fracFromClientX(e.clientX, e.currentTarget);
    setDragFrac(frac);
    onScrubbingChange?.(true);
    queuePreview(frac);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRangeRef.current === null) return;
    e.stopPropagation();
    const frac = fracFromClientX(e.clientX, e.currentTarget);
    setDragFrac(frac);
    queuePreview(frac);
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRangeRef.current === null) return;
    e.stopPropagation();
    const frac = fracFromClientX(e.clientX, e.currentTarget);
    // Drop any queued preview frame — the commit below supersedes it.
    if (previewRafRef.current !== null) {
      cancelAnimationFrame(previewRafRef.current);
      previewRafRef.current = null;
    }
    const target = fracToSeekUs(frac);
    dragRangeRef.current = null;
    setDragFrac(null);
    onScrubbingChange?.(false);
    if (target !== null) onCommitSeek(target);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore — capture may already be released
    }
  };

  // Mid-drag the local fraction drives both the fill and the countdown (the
  // committed playhead is frozen until release); at rest the committed
  // playhead does.
  const effectiveFrac = dragFrac ?? restFrac;
  const remainingSec = (spanUs * (1 - effectiveFrac)) / MICRO_SECONDS_IN_SECOND;
  const durationSec = spanUs / MICRO_SECONDS_IN_SECOND;

  // Chrome reveal, split per element: readouts persist at rest, instruments
  // reveal on approach. The PILL is a readout — paused/parked keeps it shown
  // (the current short's one at-rest duration readout), playing hides it
  // unless the pointer is over the player. The BAR is an instrument — hover-
  // only in EVERY state: position is already hover-worthy while playing (when
  // it moves), so it can't demand more while frozen, and parked at 0% it
  // carries nothing — hiding it makes the parked player mirror a rest-state
  // Grid card (clean thumbnail + corner duration badge). Both pin visible on
  // touch (no hover to ride) and mid-drag: the pointer is captured and can
  // wander outside the box, which would break :hover and fade the bar
  // in-hand.
  const hoverReveal =
    "opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100";
  const pillRevealClass =
    isPlaying && dragFrac === null ? hoverReveal : "opacity-100";
  const barRevealClass = dragFrac === null ? hoverReveal : "opacity-100";

  return (
    // Outer wrapper rides the card's contentOpacity with NO transition (it
    // tracks per-frame scroll updates); the inner wrapper keeps the
    // transitioned visible-gating. Both are static (non-positioned), so the
    // absolute children keep positioning against the player's inner clip.
    <div style={{ opacity: contentOpacity }}>
    {/* Wrapper only gates the whole instrument on being inside a short
      (playhead in a blank / edit / List fades everything out, hover or not);
      the per-element reveal classes above compose with it. */}
    <div
      className={`transition-opacity duration-150 ${
        visible ? "" : "pointer-events-none opacity-0"
      }`}
    >
      {/* Time pill, bottom-right. Its NUMBER follows the bar: bar hidden (at
        rest) → the short's total duration — a static badge over a still frame
        is the thumbnail idiom, and a lone remaining number with no bar to
        anchor it would read ambiguously; bar visible (hover / touch / drag) →
        remaining, the countdown joining the instrument that anchors it. Both
        numbers are rendered and the SAME group-hover / hover-none media
        conditions that reveal the bar swap the text, so pill and bar can
        never desync; a drag forces remaining (React state — the captured
        pointer can leave the box, breaking :hover). pointer-events-none so it
        never blocks the scrubber sharing the bottom edge (which also means
        the title tooltip is inert — kept for semantics only). */}
      {showTimePill && (
      <div
        className={`pointer-events-none absolute bottom-0 right-0 z-10 p-2 transition-opacity duration-150 ${pillRevealClass}`}
      >
        <Badge className="tabular-nums" title="Duration / time remaining">
          <Typography variant="overline">
            <span
              className={
                dragFrac === null
                  ? "group-hover:hidden [@media(hover:none)]:hidden"
                  : "hidden"
              }
            >
              {formatClock(durationSec)}
            </span>
            <span
              className={
                dragFrac === null
                  ? "hidden group-hover:inline [@media(hover:none)]:inline"
                  : "inline"
              }
            >
              {formatClock(remainingSec)}
            </span>
          </Typography>
        </Badge>
      </div>
      )}

      {/* Per-short scrubber pinned to the bottom edge: thin track that thickens
        on hover (pinned thick on touch), draggable within the short. Pointer
        handlers stopPropagation so scrubbing never toggles play. */}
      <div
        role="slider"
        aria-label="Seek within short"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(effectiveFrac * 100)}
        tabIndex={-1}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onClick={(e) => e.stopPropagation()}
        className={`absolute inset-x-0 bottom-0 z-20 flex h-4 cursor-pointer items-end transition-opacity duration-150 ${barRevealClass}`}
      >
        <div className="relative h-[3px] w-full bg-white/25 transition-[height] duration-150 group-hover:h-1.5 [@media(hover:none)]:h-1.5">
          <div
            className="absolute inset-y-0 left-0 bg-white/80"
            style={{ width: `${effectiveFrac * 100}%` }}
          />
        </div>
      </div>
    </div>
    </div>
  );
};

export default MomentPlayerScrubber;
